import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Loader2, LogOut, Paperclip, X, FileText, Film, Music, ImageIcon, Volume2, VolumeX, Settings, Mic, Square, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import AlivanAvatar from './AlivanAvatar';
import VoiceSettings from './VoiceSettings';
import SpeakingIndicator from './SpeakingIndicator';
import ProjectsButton from './projects/ProjectsButton';
import ProjectsPanel from './projects/ProjectsPanel';
import WorkCenterPanel from './work_center/WorkCenterPanel';
import SecurityPanelButton from './security/SecurityPanelButton';
import SecurityPanel from './security/SecurityPanel';
import BibleWizard from './projects/BibleWizard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getFileIcon = (contentType) => {
  if (!contentType) return FileText;
  if (contentType.startsWith('image/')) return ImageIcon;
  if (contentType.startsWith('video/')) return Film;
  if (contentType.startsWith('audio/')) return Music;
  return FileText;
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Component that securely loads an image via fetch-blob pattern (sends cookie)
const AttachmentImage = ({ fileId, filename }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let mounted = true;
    let blobUrl = null;
    (async () => {
      try {
        const resp = await fetch(`${API}/files/${fileId}`, { credentials: 'include' });
        if (!resp.ok) return;
        const blob = await resp.blob();
        blobUrl = URL.createObjectURL(blob);
        if (mounted) setSrc(blobUrl);
      } catch (e) {
        console.error('Failed to load image', e);
      }
    })();
    return () => {
      mounted = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [fileId]);

  if (!src) {
    return (
      <div className="w-full h-48 bg-black/40 border border-neon-red/20 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-neon-red animate-spin" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={filename}
      className="max-w-full max-h-96 object-contain border border-neon-red/30"
    />
  );
};

const AttachmentDisplay = ({ attachment }) => {
  const Icon = getFileIcon(attachment.content_type);
  const isImage = attachment.content_type?.startsWith('image/');

  const handleDownload = async () => {
    try {
      const resp = await fetch(`${API}/files/${attachment.file_id}`, { credentials: 'include' });
      if (!resp.ok) return;
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed', e);
    }
  };

  if (isImage) {
    return (
      <div className="mb-2">
        <AttachmentImage fileId={attachment.file_id} filename={attachment.filename} />
      </div>
    );
  }

  return (
    <button
      onClick={handleDownload}
      className="flex items-center gap-3 px-4 py-3 mb-2 bg-black/40 border border-neon-red/30 hover:border-neon-red hover:bg-black/60 transition-all duration-200 w-full text-left"
      data-testid={`attachment-${attachment.file_id}`}
    >
      <Icon className="w-5 h-5 text-neon-red shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-jetbrains text-sm text-white truncate">{attachment.filename}</div>
        <div className="font-jetbrains text-xs text-white/50">{formatSize(attachment.size)}</div>
      </div>
    </button>
  );
};

const PendingFilePreview = ({ file, onRemove }) => {
  const Icon = getFileIcon(file.type);
  const isImage = file.type?.startsWith('image/');
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (isImage) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file, isImage]);

  return (
    <div className="relative flex items-center gap-2 px-3 py-2 bg-black/60 border border-neon-red/30" data-testid="pending-file">
      {isImage && preview ? (
        <img src={preview} alt={file.name} className="w-10 h-10 object-cover" />
      ) : (
        <Icon className="w-5 h-5 text-neon-red" />
      )}
      <div className="flex-1 min-w-0 max-w-[180px]">
        <div className="font-jetbrains text-xs text-white truncate">{file.name}</div>
        <div className="font-jetbrains text-[10px] text-white/50">{formatSize(file.size)}</div>
      </div>
      <button
        onClick={onRemove}
        data-testid="remove-file-button"
        className="text-white/60 hover:text-neon-red transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const ChatInterface = ({ user, sessionId, setSessionId, onLogout }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  // Voice settings
  const [voiceSettingsOpen, setVoiceSettingsOpen] = useState(false);
  const [voiceId, setVoiceId] = useState(() => localStorage.getItem('alivan-voice-id') || 'fd0A3mGhFzxF1Wa55oPi');
  const [autoplayTTS, setAutoplayTTS] = useState(() => localStorage.getItem('alivan-autoplay-tts') !== 'false');
  const [playingMessageIdx, setPlayingMessageIdx] = useState(null);
  // Voice recording
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  // Projects panel
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [workCenterOpen, setWorkCenterOpen] = useState(false);
  const [hasActiveJobs, setHasActiveJobs] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const [hasSecurityIssues, setHasSecurityIssues] = useState(false);
  const [alivanBibleOpen, setAlivanBibleOpen] = useState(false);

  // If the user just came back from Google's OAuth callback for Gmail,
  // the backend appended `?gmail=connected|error` to the URL. Open the
  // Security panel so they immediately see the resulting state. The card
  // itself handles the toast and strips the URL param.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail') === 'connected' || params.get('gmail') === 'error') {
      setSecurityOpen(true);
    }
  }, []);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const justCreatedSessionRef = useRef(null);
  const currentAudioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const lastPlayedIdxRef = useRef(-1);
  // Audio analysis for lip-sync
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const rafIdRef = useRef(null);
  const [audioAmplitude, setAudioAmplitude] = useState(0);

  // Poll for active jobs status to update the header indicator
  useEffect(() => {
    if (!hasActiveJobs) return;
    let stopped = false;
    const check = async () => {
      try {
        const resp = await fetch(`${API}/jobs?status=running&limit=1`, { credentials: 'include' });
        const resp2 = await fetch(`${API}/jobs?status=pending&limit=1`, { credentials: 'include' });
        if (stopped) return;
        const running = resp.ok ? (await resp.json()).jobs : [];
        const pending = resp2.ok ? (await resp2.json()).jobs : [];
        setHasActiveJobs((running.length + pending.length) > 0);
      } catch {}
    };
    const t = setInterval(check, 5000);
    check();
    return () => { stopped = true; clearInterval(t); };
  }, [hasActiveJobs]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load connections snapshot once on mount for the security-panel dot indicator
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/connections`, { credentials: 'include' });
        if (!r.ok || cancelled) return;
        const data = await r.json();
        const c = data?.counts || {};
        setHasSecurityIssues((c.requires_setup || 0) + (c.error || 0) > 0);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Load user preferences from backend on mount
    (async () => {
      try {
        const resp = await fetch(`${API}/user/preferences`, { credentials: 'include' });
        if (resp.ok) {
          const prefs = await resp.json();
          if (prefs.voice_id) {
            setVoiceId(prefs.voice_id);
            localStorage.setItem('alivan-voice-id', prefs.voice_id);
          }
          if (prefs.autoplay_tts !== undefined) {
            setAutoplayTTS(prefs.autoplay_tts);
            localStorage.setItem('alivan-autoplay-tts', String(prefs.autoplay_tts));
          }
        }
      } catch (e) {
        // ignore, use localStorage defaults
      }
    })();
  }, []);

  useEffect(() => {
    // Skip loadConversation if this session was just created in this component
    if (sessionId && justCreatedSessionRef.current !== sessionId) {
      loadConversation(sessionId);
    }
  }, [sessionId]);

  const startAudioAnalysis = (audioElement) => {
    try {
      // Lazily create AudioContext (must be after a user gesture)
      if (!audioContextRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new AC();
      }
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') ctx.resume();

      // Create source and analyser
      const source = ctx.createMediaElementSource(audioElement);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.6;

      source.connect(analyser);
      analyser.connect(ctx.destination);
      analyserRef.current = analyser;

      const buffer = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(buffer);
        // Focus on vocal frequency range (mid frequencies) for better lip-sync feel
        let sum = 0;
        const start = Math.floor(buffer.length * 0.1);
        const end = Math.floor(buffer.length * 0.6);
        for (let i = start; i < end; i++) sum += buffer[i];
        const avg = sum / (end - start) / 255;
        setAudioAmplitude(avg);
        rafIdRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      console.error('Failed to start audio analysis:', e);
    }
  };

  const stopAudioAnalysis = () => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    analyserRef.current = null;
    setAudioAmplitude(0);
  };

  const playTTS = async (text, messageIdx) => {
    if (!text || !text.trim()) return;
    // Stop any current playback
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
      stopAudioAnalysis();
    }
    setPlayingMessageIdx(messageIdx);
    try {
      const resp = await fetch(`${API}/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text, voice_id: voiceId })
      });
      if (!resp.ok) {
        const err = await resp.text();
        console.error('TTS failed:', err);
        toast.error('Error al reproducir voz. Verifica los permisos de tu API key de ElevenLabs.');
        setPlayingMessageIdx(null);
        return;
      }
      const blob = await resp.blob();
      if (blob.size < 1000) {
        setPlayingMessageIdx(null);
        return;
      }
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.crossOrigin = 'anonymous';
      currentAudioRef.current = audio;

      audio.onended = () => {
        URL.revokeObjectURL(url);
        setPlayingMessageIdx(null);
        currentAudioRef.current = null;
        stopAudioAnalysis();
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        setPlayingMessageIdx(null);
        currentAudioRef.current = null;
        stopAudioAnalysis();
      };

      // Attach analyser before play so we capture data
      startAudioAnalysis(audio);
      await audio.play();
    } catch (e) {
      console.error('TTS error:', e);
      setPlayingMessageIdx(null);
      stopAudioAnalysis();
    }
  };

  const stopTTS = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
      setPlayingMessageIdx(null);
      stopAudioAnalysis();
    }
  };

  const handleSaveVoiceSettings = async ({ voice_id, autoplay_tts }) => {
    setVoiceId(voice_id);
    setAutoplayTTS(autoplay_tts);
    localStorage.setItem('alivan-voice-id', voice_id);
    localStorage.setItem('alivan-autoplay-tts', String(autoplay_tts));
    try {
      await fetch(`${API}/user/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ voice_id, autoplay_tts })
      });
    } catch (e) {
      console.error('Failed to save preferences', e);
    }
    toast.success('Configuración de voz guardada');
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(blob);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (e) {
      console.error('Failed to start recording', e);
      toast.error('No se pudo acceder al micrófono. Verifica los permisos.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (blob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', blob, 'recording.webm');
      const resp = await fetch(`${API}/stt`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      if (!resp.ok) {
        const err = await resp.text();
        console.error('STT failed:', err);
        toast.error('Error al transcribir. Verifica los permisos de tu API key.');
        return;
      }
      const data = await resp.json();
      if (data.text) {
        setInput(prev => (prev ? prev + ' ' : '') + data.text);
      }
    } catch (e) {
      console.error('Transcription error:', e);
      toast.error('Error al transcribir el audio');
    } finally {
      setIsTranscribing(false);
    }
  };

  const loadConversation = async (id) => {
    try {
      const response = await fetch(`${API}/conversations/${id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setPendingFiles(prev => [...prev, ...files]);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    const uploaded = [];
    for (const file of pendingFiles) {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await fetch(`${API}/files/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      if (!resp.ok) {
        throw new Error(`Failed to upload ${file.name}`);
      }
      const data = await resp.json();
      uploaded.push(data);
    }
    return uploaded;
  };

  const handleSend = async () => {
    if ((!input.trim() && pendingFiles.length === 0) || isLoading) return;

    setIsLoading(true);
    setIsSpeaking(true);

    let uploadedAttachments = [];
    if (pendingFiles.length > 0) {
      setIsUploading(true);
      try {
        uploadedAttachments = await uploadFiles();
      } catch (e) {
        toast.error('Error al subir archivos');
        setIsLoading(false);
        setIsSpeaking(false);
        setIsUploading(false);
        return;
      }
      setIsUploading(false);
    }

    const userMessage = {
      role: 'user',
      content: input,
      attachments: uploadedAttachments.length > 0 ? uploadedAttachments : null,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setPendingFiles([]);

    try {
      const response = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          message: input || '(Archivo adjunto)',
          file_ids: uploadedAttachments.map(a => a.file_id)
        })
      });

      if (!response.ok) throw new Error('Failed to send message');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = {
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString()
      };
      let newSessionId = sessionId;

      setMessages(prev => [...prev, assistantMessage]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.substring(6);
            try {
              const parsed = JSON.parse(data);
              if (parsed.type === 'start') {
                newSessionId = parsed.session_id;
                if (!sessionId) {
                  justCreatedSessionRef.current = newSessionId;
                  setSessionId(newSessionId);
                }
              } else if (parsed.type === 'delta') {
                assistantMessage.content += parsed.content;
                // Hide the [[AGENT_DELEGATE ...]] block from displayed content;
                // the actual delegation is confirmed by the backend `job_dispatched` event.
                const displayContent = assistantMessage.content.replace(
                  /\[\[AGENT_DELEGATE[\s\S]*$/,
                  ''
                );
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { ...assistantMessage, content: displayContent };
                  return updated;
                });
              } else if (parsed.type === 'job_dispatched') {
                // Attach the job to the current assistant message so it renders inline
                setMessages(prev => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last && last.role === 'assistant') {
                    updated[updated.length - 1] = {
                      ...last,
                      job_id: parsed.job.job_id,
                      agent_id: parsed.job.agent_id,
                      project_slug: parsed.job.project_slug,
                    };
                  }
                  return updated;
                });
                setHasActiveJobs(true);
              } else if (parsed.type === 'done') {
                setIsLoading(false);
                setIsSpeaking(false);
                // Strip any AGENT_DELEGATE block from the final saved content
                assistantMessage.content = assistantMessage.content.replace(
                  /\[\[AGENT_DELEGATE[\s\S]*?\]\]\s*$/,
                  ''
                ).trim();
                setMessages(prev => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last && last.role === 'assistant') {
                    updated[updated.length - 1] = { ...last, content: assistantMessage.content };
                  }
                  return updated;
                });
                // Auto-play TTS if enabled
                if (autoplayTTS && assistantMessage.content) {
                  // Use setTimeout to allow state to settle
                  setTimeout(() => {
                    setMessages(currentMessages => {
                      const lastIdx = currentMessages.length - 1;
                      if (lastPlayedIdxRef.current !== lastIdx) {
                        lastPlayedIdxRef.current = lastIdx;
                        playTTS(assistantMessage.content, lastIdx);
                      }
                      return currentMessages;
                    });
                  }, 100);
                }
              } else if (parsed.type === 'error') {
                toast.error('Error: ' + parsed.message);
                setIsLoading(false);
                setIsSpeaking(false);
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error al enviar el mensaje.');
      setIsLoading(false);
      setIsSpeaking(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const firstName = user.name.split(' ')[0];

  return (
    <div className="flex flex-col h-screen bg-[#0A0A0A]" data-testid="chat-interface">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 px-6 py-4 backdrop-blur-xl bg-black/60 border-b border-neon-red/20">
        <div className="flex items-center gap-4">
          <AlivanAvatar isSpeaking={isSpeaking || playingMessageIdx !== null} amplitude={playingMessageIdx !== null ? audioAmplitude : 0} size="md" />
          <div>
            <h1 className="font-outfit text-2xl font-bold text-white" data-testid="chat-title">Alivan</h1>
            <p className="font-jetbrains text-sm text-white/60">Asistente de IA Cibernética</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-3">
            {user.picture && (
              <img
                src={user.picture}
                alt={user.name}
                className="w-9 h-9 rounded-full border border-neon-red/40"
                data-testid="user-avatar"
              />
            )}
            <div className="text-right">
              <p className="font-outfit text-sm font-semibold text-white" data-testid="user-name">{user.name}</p>
              <p className="font-jetbrains text-xs text-white/50">{user.email}</p>
            </div>
          </div>
          <button
            onClick={() => setAutoplayTTS(v => {
              const nv = !v;
              localStorage.setItem('alivan-autoplay-tts', String(nv));
              return nv;
            })}
            data-testid="toggle-autoplay-header"
            className={`p-2.5 backdrop-blur-xl border transition-all duration-300 ${
              autoplayTTS
                ? 'bg-neon-red/20 border-neon-red text-neon-red'
                : 'bg-black/40 border-neon-red/30 text-white hover:border-neon-red'
            }`}
            title={autoplayTTS ? 'Auto-play activado' : 'Auto-play desactivado'}
          >
            {autoplayTTS ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
          <ProjectsButton
            onClick={() => setProjectsOpen(true)}
            hasActiveJobs={hasActiveJobs}
          />
          <button
            onClick={() => setWorkCenterOpen(true)}
            data-testid="open-work-center-button"
            className="p-2.5 backdrop-blur-xl bg-black/40 border border-neon-red/30 hover:border-neon-red hover:bg-neon-red/20 text-white transition-all duration-300"
            title="Centro de Trabajo — iniciativa autónoma con aprobación"
          >
            <span className="font-jetbrains text-[10px] tracking-widest uppercase">WC</span>
          </button>
          <SecurityPanelButton
            onClick={() => setSecurityOpen(true)}
            hasIssues={hasSecurityIssues}
          />
          <button
            onClick={() => setAlivanBibleOpen(true)}
            data-testid="open-alivan-bible-button"
            className="p-2.5 backdrop-blur-xl bg-black/40 border border-neon-red/30 hover:border-neon-red hover:bg-neon-red/20 text-white transition-all duration-300"
            title="AliVan Bible (personalidad configurable)"
          >
            <span className="font-jetbrains text-[10px] tracking-widest uppercase">AB</span>
          </button>
          <button
            onClick={() => setVoiceSettingsOpen(true)}
            data-testid="open-voice-settings"
            className="p-2.5 backdrop-blur-xl bg-black/40 border border-neon-red/30 hover:border-neon-red hover:bg-neon-red/20 text-white transition-all duration-300"
            title="Configuración de voz"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="p-2.5 backdrop-blur-xl bg-black/40 border border-neon-red/30 hover:border-neon-red hover:bg-neon-red/20 text-white transition-all duration-300"
            title="Cerrar sesión"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      <VoiceSettings
        open={voiceSettingsOpen}
        onClose={() => setVoiceSettingsOpen(false)}
        voiceId={voiceId}
        autoplayTTS={autoplayTTS}
        onSave={handleSaveVoiceSettings}
      />

      <SpeakingIndicator
        visible={playingMessageIdx !== null}
        amplitude={audioAmplitude}
        onStop={stopTTS}
      />

      <ProjectsPanel
        open={projectsOpen}
        onClose={() => setProjectsOpen(false)}
      />

      <WorkCenterPanel
        open={workCenterOpen}
        onClose={() => setWorkCenterOpen(false)}
      />

      <SecurityPanel
        open={securityOpen}
        onClose={() => {
          setSecurityOpen(false);
          // Refresh the dot indicator after any interaction with the panel
          fetch(`${API}/connections`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
              if (!data) return;
              const c = data.counts || {};
              setHasSecurityIssues((c.requires_setup || 0) + (c.error || 0) > 0);
            })
            .catch(() => {});
        }}
      />

      <BibleWizard
        open={alivanBibleOpen}
        onClose={() => setAlivanBibleOpen(false)}
        projectSlug="alivan_self"
        projectName="AliVan Bible"
      />

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6" data-testid="messages-container">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md">
              <div className="flex justify-center">
                <AlivanAvatar isSpeaking={false} size="lg" />
              </div>
              <p className="mt-6 font-outfit text-2xl text-white" data-testid="welcome-heading">
                Hola, {firstName}
              </p>
              <p className="mt-2 font-jetbrains text-sm text-white/60">
                Estoy lista para conversar. Puedes enviarme mensajes, imágenes, documentos, audio o video.
              </p>
            </div>
          </div>
        )}
        <AnimatePresence initial={false}>
          {messages.map((message, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              data-testid={`message-${message.role}-${index}`}
            >
              {message.role === 'assistant' && (
                <div className="mr-3 mt-1">
                  <AlivanAvatar
                    isSpeaking={(isSpeaking && index === messages.length - 1) || playingMessageIdx === index}
                    amplitude={playingMessageIdx === index ? audioAmplitude : 0}
                    size="sm"
                  />
                </div>
              )}
              <div
                className={`max-w-[70%] px-6 py-4 font-jetbrains text-base leading-relaxed relative group ${
                  message.role === 'user'
                    ? 'bg-[#1A1A1A] text-white border-r-2 border-neon-red/50'
                    : 'bg-[#141414] text-white border-l-2 border-neon-red/30'
                }`}
              >
                {message.attachments && message.attachments.length > 0 && (
                  <div className="mb-3 space-y-2">
                    {message.attachments.map((att) => (
                      <AttachmentDisplay key={att.file_id} attachment={att} />
                    ))}
                  </div>
                )}
                {message.content && <div className="whitespace-pre-wrap">{message.content}</div>}
                {message.role === 'assistant' && message.job_id && (
                  <button
                    onClick={() => setProjectsOpen(true)}
                    data-testid={`inline-job-${message.job_id}`}
                    className="mt-3 flex items-center gap-2 px-3 py-2 bg-neon-red/10 border border-neon-red/40 hover:bg-neon-red/20 transition-colors w-full text-left"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-neon-red animate-pulse" />
                    <span className="font-jetbrains text-xs text-white/90">
                      Preparando trabajo en <span className="text-neon-red font-semibold">
                        {message.project_slug === 'mazal_kids' ? 'Mazal Kids' : 'El Último Testigo'}
                      </span>
                      <span className="text-white/50 ml-1">· ver en Proyectos</span>
                    </span>
                  </button>
                )}
                {message.role === 'assistant' && message.content && (
                  <button
                    onClick={() => playingMessageIdx === index ? stopTTS() : playTTS(message.content, index)}
                    data-testid={`tts-message-${index}`}
                    className="absolute -bottom-3 -right-3 p-2 bg-black border border-neon-red/40 text-neon-red hover:bg-neon-red hover:text-white transition-all duration-200 opacity-70 hover:opacity-100 shadow-[0_0_10px_rgba(255,0,51,0.3)]"
                    title={playingMessageIdx === index ? 'Detener' : 'Reproducir voz'}
                  >
                    {playingMessageIdx === index ? <Square className="w-3.5 h-3.5" fill="currentColor" /> : <Volume2 className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        <div ref={messagesEndRef} />
      </div>

      {/* Pending files preview */}
      {pendingFiles.length > 0 && (
        <div className="px-6 py-3 bg-black/40 border-t border-neon-red/10">
          <div className="max-w-4xl mx-auto flex flex-wrap gap-2">
            {pendingFiles.map((file, index) => (
              <PendingFilePreview
                key={index}
                file={file}
                onRemove={() => removePendingFile(index)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="px-6 py-4 backdrop-blur-xl bg-black/60 border-t border-neon-red/20">
        <div className="max-w-4xl mx-auto flex gap-3 items-center">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            className="hidden"
            multiple
            accept="image/*,video/*,audio/*,.pdf,.txt,.doc,.docx,.mp3,.mp4"
            data-testid="file-input"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            data-testid="attach-file-button"
            className="p-4 bg-[#141414] text-white border border-neon-red/20 hover:border-neon-red hover:bg-neon-red/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
            title="Adjuntar archivo"
          >
            <Paperclip className="w-5 h-5" />
          </button>
          <button
            onClick={isRecording ? stopRecording : startRecording}
            disabled={isLoading || isTranscribing}
            data-testid="mic-button"
            className={`p-4 border transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
              isRecording
                ? 'bg-neon-red text-white border-neon-red animate-pulse shadow-[0_0_20px_rgba(255,0,51,0.6)]'
                : 'bg-[#141414] text-white border-neon-red/20 hover:border-neon-red hover:bg-neon-red/10'
            }`}
            title={isRecording ? 'Detener grabación' : 'Grabar mensaje de voz'}
          >
            {isTranscribing ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : isRecording ? (
              <Square className="w-5 h-5" fill="currentColor" />
            ) : (
              <Mic className="w-5 h-5" />
            )}
          </button>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isUploading ? "Subiendo archivos..." : "Escribe tu mensaje..."}
            disabled={isLoading}
            data-testid="chat-input"
            className="flex-1 px-6 py-4 bg-[#141414] text-white font-jetbrains border border-neon-red/20 focus:border-neon-red focus:outline-none transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <button
            onClick={handleSend}
            disabled={isLoading || (!input.trim() && pendingFiles.length === 0)}
            data-testid="send-button"
            className="px-8 py-4 bg-neon-red text-white font-outfit font-semibold hover:bg-neon-red/80 disabled:bg-neon-red/30 disabled:cursor-not-allowed transition-all duration-300 shadow-[0_0_20px_rgba(255,0,51,0.3)] hover:shadow-[0_0_30px_rgba(255,0,51,0.6)]"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
